// How each AI tool connects to cortex (ADR 0030): one recipe per tool, for the addresses of one cortex. Cortex is
// the same for every tool; only the place a tool reads its servers from differs. The Connect page and the
// developer guide both render this table.

/** The name a tool lists cortex's MCP under (and the plugin's name). */
export const MCP_SERVER_NAME = 'cortex';

/** What a recipe connects to: an MCP address, and this cortex's plugin marketplace when the one MCP is chosen. */
export interface ConnectTarget {
  /** The MCP address: the one MCP (`/mcp`) or a project's own (`/mcp/<org>/<project>`). */
  mcp: string;
  /**
   * The Claude Code plugin marketplace (its URL and name), offered for the one MCP that may write (the plugin
   * carries that address); null: no plugin.
   */
  marketplace: { url: string; name: string } | null;
}

export interface Connector {
  id: string;
  label: string;
  /** Where the code goes, in a few words ("In Claude Code, run:"). */
  where(t: ConnectTarget): string;
  /** What to paste (a command or a config file). */
  code(t: ConnectTarget): string;
  /** Short lines after it (what it installs, where else it can go); often none. */
  notes(t: ConnectTarget): string[];
  /** Another way to the same connection (Claude Code without the plugin), or null. */
  alternative(t: ConnectTarget): { label: string; code: string } | null;
}

/** Said once under every recipe: how a tool signs in. */
export const SIGN_IN_NOTE = 'The first time, your browser opens to sign in to cortex.';

const claudeMcpAdd = (url: string) => `claude mcp add --transport http ${MCP_SERVER_NAME} ${url}`;
const none = () => null;

export const CONNECTORS: readonly Connector[] = [
  {
    id: 'claude-code',
    label: 'Claude Code',
    where: (t) => (t.marketplace ? 'In Claude Code, run:' : 'In a terminal, run:'),
    code: (t) =>
      t.marketplace
        ? `/plugin marketplace add ${t.marketplace.url}\n/plugin install ${MCP_SERVER_NAME}@${t.marketplace.name}`
        : claudeMcpAdd(t.mcp),
    notes: (t) =>
      t.marketplace
        ? [
            "It installs cortex's plugin: the connection and a skill for working in cortex projects.",
            `Keep it up to date: /plugin → Marketplaces → ${t.marketplace.name} → Enable auto-update.`,
          ]
        : [],
    alternative: (t) =>
      t.marketplace ? { label: 'Without the plugin, in a terminal', code: claudeMcpAdd(t.mcp) } : null,
  },
  {
    id: 'codex',
    label: 'Codex',
    where: () => 'In a terminal, run:',
    code: (t) => `codex mcp add ${MCP_SERVER_NAME} --url ${t.mcp}\ncodex mcp login ${MCP_SERVER_NAME}`,
    notes: () => [],
    alternative: none,
  },
  {
    id: 'cursor',
    label: 'Cursor',
    where: () => 'Put this in .cursor/mcp.json in your repo:',
    code: (t) => JSON.stringify({ mcpServers: { [MCP_SERVER_NAME]: { url: t.mcp } } }, null, 2),
    notes: () => ['Or in ~/.cursor/mcp.json, for every repo.'],
    alternative: none,
  },
  {
    id: 'vscode',
    label: 'VS Code',
    where: () => 'Put this in .vscode/mcp.json in your repo:',
    code: (t) => JSON.stringify({ servers: { [MCP_SERVER_NAME]: { type: 'http', url: t.mcp } } }, null, 2),
    notes: () => [],
    alternative: none,
  },
  {
    id: 'other',
    label: 'Other',
    where: () => 'Give your tool this address:',
    code: (t) => t.mcp,
    notes: () => [
      'Any MCP tool with Streamable HTTP and OAuth sign-in; it finds where to sign in by itself.',
    ],
    alternative: none,
  },
];
